import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  alpha,
  useTheme,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  RestoreFromTrash,
  StickyNote2,
  Assignment,
  Info,
} from "@mui/icons-material";
import { useRecoverNotes } from "../../../../hooks/tableNotes/useRecoverNotes";
import { useRecoverWitness } from "../../../../hooks/tableNotes/useRecoverWitness";

const RecyclingBin = ({
  token,
  open = false,
  onClose,
  id_documento,
  onRestore,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletedNotes, setDeletedNotes] = useState([]);
  const [deletedWitnesses, setDeletedWitnesses] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [fecha, setFecha] = useState(new Date().toLocaleDateString("sv-SE"));

  const {
    getDeletedNotes,
    putRecoverNote,
    loading: loadingNotes,
  } = useRecoverNotes(token, id_documento, fecha);
  const {
    getDeletedWitness,
    putRecoverWitness,
    loading: loadingWitnesses,
  } = useRecoverWitness(token, id_documento, fecha);

  useEffect(() => {
    if (open && id_documento) {
      getDeletedNotes()
        .then((data) => setDeletedNotes(data))
        .catch((err) =>
          console.error("[RecyclingBin] Error al cargar notas:", err)
        );

      getDeletedWitness()
        .then((data) => setDeletedWitnesses(data))
        .catch((err) =>
          console.error("[RecyclingBin] Error al cargar los testigos", err)
        );
    }
  }, [open, id_documento, fecha, getDeletedNotes, getDeletedWitness]);

  useEffect(() => {
    const items = tabValue === 0 ? deletedNotes : deletedWitnesses;
    const q = searchQuery.trim().toLowerCase();

    const filtered = items.filter((item) => {
      if (tabValue === 0) {
        const matchTitle = item.title?.toLowerCase().includes(q);
        const matchContent = item.content?.toLowerCase().includes(q);
        
        const matchTestigo =
          Array.isArray(item.testigos) &&
          item.testigos
            .flat()
            .some(
              (t) =>
                typeof t.medio === 'string' &&
                t.medio.toLowerCase().includes(q)
            );
        return matchTitle || matchContent || matchTestigo;
      } else {
        const matchMedio = item.medio?.toLowerCase().includes(q);
        const matchTitulo = item.title?.toLowerCase().includes(q);
        const matchAutor = item.content?.toLowerCase().includes(q);
        return matchMedio || matchTitulo || matchAutor;
      }
    });

    setFilteredItems(q === "" ? items : filtered);
  }, [searchQuery, tabValue, deletedNotes, deletedWitnesses]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchQuery("");
  };

  const handleRestore = async (ids) => {
    if (tabValue === 0) {
      try {
        await Promise.all(ids.map((id) => putRecoverNote(id)));
        const updated = await getDeletedNotes();
        setDeletedNotes(updated);
      } catch (err) {
        console.error("[RecyclingBin] Error al restaurar nota:", err);
      }
    } else {
      try {
        await Promise.all(ids.map((idRef) => putRecoverWitness(idRef)));
        const updated = await getDeletedWitness();
        setDeletedWitnesses(updated);
      } catch (err) {
        console.error("[RecyclingBin] Error al restaurar testigo:", err);
      }
    }

    if (onRestore) onRestore();
  };

  const loading = loadingNotes || loadingWitnesses;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          px: 3,
          py: 2,
        }}
      >
        <Box display="flex" alignItems="center">
          <RestoreFromTrash sx={{ mr: 1.5 }} />
          <Typography variant="h6">Papelera de Reciclaje</Typography>
        </Box>
      </DialogTitle>

      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <TextField
          label="Filtrar por fecha de eliminación"
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            icon={<StickyNote2 sx={{ mr: 1 }} />}
            iconPosition="start"
            label={`Notas (${deletedNotes.length})`}
          />
          <Tab
            icon={<Assignment sx={{ mr: 1 }} />}
            iconPosition="start"
            label={`Testigos (${deletedWitnesses.length})`}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, height: 400, overflow: "auto" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando...</Typography>
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: theme.palette.text.secondary,
              p: 3,
            }}
          >
            <Info sx={{ fontSize: 60, mb: 2, opacity: 0.7 }} />
            <Typography variant="h6" gutterBottom>
              No hay elementos eliminados
            </Typography>
            <Typography variant="body2" align="center">
              Los elementos que elimines aparecerán aquí durante 30 días
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {tabValue === 0
              ? filteredItems.map((nota, index) => (
                  <React.Fragment key={nota.id}>
                    <ListItem
                      button
                      sx={{
                        py: 1.5,
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.action.hover,
                            0.1
                          ),
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{ fontWeight: 500 }}
                            >
                              {nota.title || "Nota sin título"}
                            </Typography>
                            <Tooltip title="Restaurar nota">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestore([nota.id]);
                                }}
                              >
                                <RestoreFromTrash
                                  fontSize="small"
                                  sx={{ color: "green" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              noWrap
                              sx={{ mb: 1 }}
                            >
                              {nota.content || "Sin contenido"}
                            </Typography>

                            <Divider sx={{ my: 1 }}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                noWrap
                              >
                                Testigos:
                              </Typography>
                            </Divider>

                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {Array.isArray(nota.testigos) && nota.testigos.length > 0 ? (
                                nota.testigos
                                .flat()
                                .map((t, idx) => (
                                  <Chip
                                    key={idx}
                                    label={t.medio || "Sin medio"}
                                    size="small"
                                    sx={{
                                      backgroundColor: "rgba(25, 118, 210, 0.1)",
                                      color: "#1976d2",
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  Sin testigos
                                </Typography>
                              )}
                            </Box>
                          </>
                        }
                      />
                    </ListItem>
                    {index < filteredItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              : filteredItems.map((testigo, index) => (
                  <React.Fragment key={testigo.id}>
                    <ListItem
                      button
                      sx={{
                        py: 1.5,
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.action.hover,
                            0.1
                          ),
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{ fontWeight: 500 }}
                            >
                              {testigo.title || "Testigo sin dato"}
                            </Typography>
                            <Tooltip title="Restaurar testigo">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestore([testigo.id]);
                                }}
                              >
                                <RestoreFromTrash
                                  fontSize="small"
                                  sx={{ color: "green" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{ fontWeight: 500 }}
                            >
                              {testigo.content || "Testigo sin dato"}
                            </Typography>
                            <Divider sx={{ my: 1 }}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                noWrap
                              >
                                Testigos:
                              </Typography>
                            </Divider>

                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                label={testigo.medio || "Sin medio"}
                                size="small"
                                sx={{
                                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                                  color: "#1976d2",
                                }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
          </List>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "end",
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecyclingBin;
